import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Link as LinkIcon, Copy, X, ShieldOff, Clock, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Active share links widget.
 *
 * Mounted inside the Bookings tab. Lists every live (non-revoked,
 * non-expired) safety share link the provider has issued so they
 * can see at a glance which appointments still have eyes on them,
 * copy the URL again, and revoke individual links with one tap.
 *
 * Auto-refreshes every 60s so the &ldquo;3 people checked in&rdquo; counter
 * stays current during a long appointment.
 */
export default function ActiveShareLinksSection() {
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/safety/share-links');
      setLinks(res.data || []);
    } catch {
      setLinks([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const handleRevoke = async (token, label) => {
    if (!window.confirm(`Revoke the share link for ${label}? Anyone with the URL will lose access immediately.`)) return;
    try {
      await api.delete(`/safety/share-links/${token}`);
      toast.success('Share link revoked');
      load();
    } catch {
      toast.error('Could not revoke link');
    }
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  /** Compact relative-time helper. */
  const relTime = (iso) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms) || ms < 0) return 'just now';
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  /** Hours-until or &ldquo;expired&rdquo;. */
  const expiresLabel = (iso) => {
    if (!iso) return '';
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 'expired';
    const hrs = Math.floor(ms / 3600000);
    if (hrs >= 24) return `${Math.floor(hrs / 24)}d left`;
    if (hrs >= 1) return `${hrs}h left`;
    return `${Math.max(1, Math.floor(ms / 60000))} min left`;
  };

  if (loading) return null;
  if (!links || links.length === 0) return null;

  return (
    <div className="card-surface p-5 mb-6" data-testid="active-share-links-section">
      <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
        <LinkIcon className="h-4 w-4 text-[#D4AF37]" />
        Active safety share links
        <span className="text-xs text-stone-400 font-normal">
          ({links.length} live)
        </span>
      </h3>
      <ul className="space-y-2">
        {links.map((l) => (
          <li
            key={l.token}
            className="p-3 bg-[#F9F6F0] rounded-lg"
            data-testid={`share-link-row-${l.token}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-[#1A1A1A]">
                    {l.service_name || 'Booking'}
                    <span className="text-stone-400"> with {l.client_first_name}</span>
                  </p>
                  {l.mode === 'quiet' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-stone-700 bg-white border border-stone-300 px-1.5 py-0.5 rounded-full">
                      <EyeOff className="h-3 w-3" />Quiet
                    </span>
                  )}
                </div>
                <p className="text-xs text-stone-500 mt-0.5">
                  {l.date} at {l.time}
                  <span className="text-stone-400"> &middot; </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />{expiresLabel(l.expires_at)}
                  </span>
                </p>
                {l.contact_acks_count > 0 && (
                  <p
                    className="text-[11px] text-emerald-700 mt-1.5"
                    data-testid={`share-link-acks-${l.token}`}
                  >
                    {l.contact_acks_count} {l.contact_acks_count === 1 ? 'person has' : 'people have'} checked in
                    {l.contact_acks_recent?.length > 0 && (
                      <span className="text-stone-500 font-normal">
                        : {l.contact_acks_recent.map(a => `${a.name} (${relTime(a.ack_at)})`).join(', ')}
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCopy(l.url)}
                  className="h-7 px-2 text-xs border-[#E6E4DD] gap-1"
                  data-testid={`copy-share-link-${l.token}`}
                  title="Copy URL"
                >
                  <Copy className="h-3 w-3" />Copy
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRevoke(l.token, `${l.service_name || 'this booking'} with ${l.client_first_name}`)}
                  className="h-7 px-2 text-xs text-red-500 border-red-200 hover:bg-red-50 gap-1"
                  data-testid={`revoke-share-link-${l.token}`}
                  title="Revoke this link"
                >
                  <ShieldOff className="h-3 w-3" />Revoke
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
